import * as React from "react";
import { Google } from "@mui/icons-material";
import { Box, Button, keyframes, Link, TextField, Typography, useTheme } from "@mui/material";
import loginBgImage from "@app/assets/img/login-background.png";
import Session from "@app/util/Session";
const fadeIn = keyframes `
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;
const pulse = keyframes `
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 169, 223, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(0, 169, 223, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 169, 223, 0); }
`;
const LoginForm = () => {
    var _a;
    const theme = useTheme();
    React.useEffect(() => {
        var _a;
        if ((_a = Session.activeUser) === null || _a === void 0 ? void 0 : _a.email) {
            window.location.href = "/logout/";
        }
    }, []);
    const [showEmailLogin, setShowEmailLogin] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const [code, setCode] = React.useState("");
    const [codeSent, setCodeSent] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const toggleEmailLogin = React.useCallback(() => {
        setShowEmailLogin((prev) => !prev);
        setErrorMessage(null);
        setCodeSent(false);
        setCode("");
    }, []);
    const handleEmailChange = React.useCallback((event) => {
        setEmail(event.target.value);
    }, []);
    const handleCodeChange = React.useCallback((event) => {
        setCode(event.target.value);
    }, []);
    const sendCode = React.useCallback(() => {
        setBusy(true);
        setErrorMessage(null);
        fetch("/api/login/email-code/request", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
        })
            .then((response) => {
            if (!response.ok) {
                throw new Error("request failed");
            }
            setCodeSent(true);
        })
            .catch(() => {
            setErrorMessage("Something went wrong. Please try again.");
        })
            .finally(() => {
            setBusy(false);
        });
    }, [email]);
    const verifyCode = React.useCallback(() => {
        setBusy(true);
        setErrorMessage(null);
        fetch("/api/login/email-code/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, code }),
        })
            .then((response) => {
            if (!response.ok) {
                throw new Error("verify failed");
            }
            window.location.assign("/");
            setBusy(false);
        })
            .catch(() => {
            setErrorMessage("Invalid or expired code.");
            setBusy(false);
        });
    }, [email, code]);
    const handleSubmit = codeSent ? verifyCode : sendCode;
    if ((_a = Session.activeUser) === null || _a === void 0 ? void 0 : _a.email) {
        return null;
    }
    return (React.createElement(Box, { sx: {
            height: "100dvh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.1)), url(${loginBgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            overflow: "hidden",
            fontFamily: "'Nunito', 'Roboto', sans-serif",
        } },
        React.createElement(Box, { sx: {
                textAlign: "center",
                padding: "2.5rem",
                maxWidth: "400px",
                width: "70%",
                background: "linear-gradient(180deg,rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 1) 25%, rgba(255, 255, 255, 1) 100%);",
                // border: `1px solid ${theme.palette.border}`,
                borderRadius: "1.5rem",
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.25)",
                backdropFilter: "blur(20px) saturate(160%)",
                animation: `${fadeIn} 0.8s ease-out`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            } },
            React.createElement(Box, { sx: {
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "0.4rem 0.8rem",
                    background: theme.palette.mode === "dark" ? "rgba(0, 169, 223, 0.1)" : "rgba(0, 169, 223, 0.08)",
                    color: theme.palette.primary.main,
                    borderRadius: "2rem",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    marginBottom: "1.5rem",
                    textTransform: "uppercase",
                } },
                React.createElement(Box, { sx: {
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "50%",
                        marginRight: "8px",
                        animation: `${pulse} 2s infinite`,
                    } }),
                "Secure Portal Access"),
            React.createElement(Box, { sx: {
                    marginBottom: "2rem",
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "& svg": {
                        width: "52px",
                        height: "58px",
                    },
                } },
                React.createElement("svg", { width: "122", height: "127", viewBox: "0 0 122 127", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    React.createElement("path", { 
                        // eslint-disable-next-line max-len
                        d: "M118.298 60.8511C118.298 53.2791 116.809 45.7813 113.917 38.7857C111.024 31.7902 106.785 25.4338 101.441 20.0796C96.0965 14.7254 89.7519 10.4783 82.7692 7.5806C75.7866 4.68294 68.3026 3.19153 60.7446 3.19153C53.1866 3.19153 45.7026 4.68294 38.7199 7.5806C31.7373 10.4783 25.3927 14.7254 20.0483 20.0796C14.704 25.4338 10.4647 31.7902 7.57238 38.7857C4.68006 45.7813 3.19141 53.2791 3.19141 60.8511", stroke: theme.palette.primary.main, strokeWidth: "6.38298" }),
                    React.createElement("path", { 
                        // eslint-disable-next-line max-len
                        d: "M118.298 65.5318C118.298 73.1038 116.809 80.6016 113.917 87.5972C111.024 94.5928 106.785 100.949 101.441 106.303C96.0965 111.657 89.7519 115.905 82.7692 118.802C75.7866 121.7 68.3026 123.191 60.7446 123.191C53.1866 123.191 45.7026 121.7 38.7199 118.802C31.7373 115.905 25.3927 111.657 20.0483 106.303C14.704 100.949 10.4647 94.5928 7.57238 87.5972C4.68006 80.6016 3.19141 73.1038 3.19141 65.5318", stroke: theme.palette.primary.main, strokeWidth: "6.38298" }),
                    React.createElement("path", { d: "M43.6172 60.8508V39.9998H63.83C69.5746 39.9998 82.5534 46.2125 82.5534 60.8508", stroke: theme.palette.primary.main, strokeWidth: "6.38298" }),
                    React.createElement("path", { d: "M43.6172 65.5322V86.3833H63.83C69.5746 86.3833 82.5534 80.1705 82.5534 65.5322", stroke: theme.palette.primary.main, strokeWidth: "6.38298" }))),
            React.createElement(Typography, { variant: "h1", sx: {
                    fontWeight: 700,
                    fontSize: "1.5rem",
                    margin: "0 0 0.5rem 0",
                    color: theme.palette.text.primary,
                    lineHeight: 1.2,
                } }, "Sign in to your Agent Portal"),
            React.createElement(Typography, { sx: {
                    color: theme.palette.text.secondary,
                    fontSize: "1rem",
                    lineHeight: 1.5,
                    marginBottom: "2rem",
                } }, "Access your real estate agent portal with your Google account."),
            React.createElement(Button, { fullWidth: true, color: "primary", component: Link, href: LOGIN_URL, variant: "contained", startIcon: React.createElement(Google, { sx: { fontSize: "14px !important" } }), sx: {
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12px",
                    fontFamily: "'Nunito', 'Roboto', sans-serif",
                    textTransform: "uppercase",
                    boxShadow: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                        transform: "translateY(-1px)",
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.12)",
                    },
                    "&:active": {
                        transform: "translateY(0)",
                    },
                } }, "Continue with Google"),
            React.createElement(Link, { component: "button", type: "button", onClick: toggleEmailLogin, sx: { display: "block", marginTop: "1rem", fontSize: "0.8rem" } }, showEmailLogin ? "Back to Google sign-in" : "Outside collaborator? Sign in with email code"),
            showEmailLogin && (React.createElement(Box, { sx: { marginTop: "1rem", textAlign: "left" } },
                React.createElement(TextField, { fullWidth: true, size: "small", label: "Email address", type: "email", value: email, onChange: handleEmailChange, disabled: codeSent, sx: { marginBottom: "0.75rem" } }),
                codeSent && (React.createElement(TextField, { fullWidth: true, size: "small", label: "6-digit code", value: code, onChange: handleCodeChange, sx: { marginBottom: "0.75rem" } })),
                errorMessage && (React.createElement(Typography, { color: "error", sx: { fontSize: "0.8rem", marginBottom: "0.5rem" } }, errorMessage)),
                codeSent && (React.createElement(Typography, { sx: { fontSize: "0.75rem", color: "text.secondary", marginBottom: "0.5rem" } }, "If that address has portal access, a code is on its way.")),
                React.createElement(Button, { fullWidth: true, variant: "contained", disabled: busy || !email, onClick: handleSubmit }, codeSent ? "Verify code" : "Send code"))))));
};
export default LoginForm;
